// src/pages/VerificarCertificado.js
//
// Página pública (sin auth) a la que apunta el QR del certificado.
// URL: /cert/:codigo
//
// Muestra:
//   - Banner ✓/✗ con el estado del certificado
//   - Datos verificados: estudiante, documento enmascarado, curso,
//     calificación, institución emisora, fecha
//   - Visualización del certificado (CertificadoCard) con botón para
//     descargar PNG o imprimir
//   - Advertencia explícita si el backend detectó tampering (hash no
//     coincide con los datos del snapshot)
//
// El backend ya registra el escaneo del QR en certificado_verificacion.

import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Container, Paper, Typography, Button, CircularProgress,
  Alert, Chip, Stack, Divider,
} from '@mui/material';
import {
  Verified, ErrorOutline, Download, Print, OpenInNew,
} from '@mui/icons-material';
import { certificadoService } from '../services/api';
import CertificadoCard, {
  descargarCertificadoPng, imprimirCertificado,
} from '../components/certificado/CertificadoCard';

export default function VerificarCertificado() {
  const { codigo } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: '' });
  const cardRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const data = await certificadoService.validar(codigo);
        setState({ loading: false, data, error: '' });
      } catch (err) {
        setState({ loading: false, data: null, error: err.message || 'No se pudo verificar el certificado.' });
      }
    })();
  }, [codigo]);

  // URL de validación — la misma de esta página. Si llegan por otro
  // dominio (ej. el admin configuró uno distinto en configuracion_sistema)
  // igual queda la URL del QR consistente.
  const urlValidacion = `${window.location.origin}/cert/${codigo}`;

  if (state.loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (state.error || !state.data) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <ErrorOutline sx={{ fontSize: 60, color: 'error.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom>Certificado no encontrado</Typography>
          <Typography color="text.secondary">
            {state.error || `No pudimos verificar el código "${codigo}". Es posible que el certificado haya sido revocado o que el código sea incorrecto.`}
          </Typography>
        </Paper>
      </Container>
    );
  }

  const { valido, tampering, estado, mensaje, certificado, plantilla } = state.data;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f6f8', py: { xs: 2, md: 6 } }}>
      <Container maxWidth="lg">
        {/* Banner de resultado */}
        <Paper sx={{
          p: { xs: 2, md: 3 }, mb: 3,
          bgcolor: valido ? 'success.main' : (tampering ? 'error.main' : 'warning.main'),
          color: '#fff',
        }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            {valido
              ? <Verified sx={{ fontSize: 48 }} />
              : <ErrorOutline sx={{ fontSize: 48 }} />}
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                {valido ? '✓ Certificado válido' : '✗ Certificado no válido'}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.95 }}>{mensaje}</Typography>
            </Box>
            <Chip label={estado} sx={{ bgcolor: 'rgba(255,255,255,0.25)', color: '#fff', fontWeight: 600 }} />
          </Stack>

          {tampering && (
            <Alert severity="error" sx={{ mt: 2 }}>
              <strong>Advertencia:</strong> los datos de este certificado no coinciden con la firma digital
              registrada al momento de emisión. Pudieron haber sido alterados.
            </Alert>
          )}
        </Paper>

        {/* Resumen de datos verificados */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="overline" color="text.secondary">Datos verificados</Typography>
          <Divider sx={{ my: 1.5 }} />
          <Stack spacing={1.5}>
            <Dato label="Titular" value={certificado.estudianteNombre} />
            {certificado.estudianteDocumento && (
              <Dato label="Documento" value={certificado.estudianteDocumento} />
            )}
            <Dato label="Curso" value={certificado.cursoTitulo} />
            {certificado.cursoDuracion && (
              <Dato label="Duración" value={certificado.cursoDuracion} />
            )}
            {certificado.calificacion != null && (
              <Dato label="Calificación" value={`${Number(certificado.calificacion).toFixed(1)} / 100`} />
            )}
            <Dato label="Institución emisora" value={certificado.institucionNombre} />
            <Dato label="Fecha de emisión"
                  value={new Date(certificado.fechaEmision).toLocaleDateString('es-CO', {
                    day: '2-digit', month: 'long', year: 'numeric',
                  })} />
            <Dato label="Código" value={certificado.codigo} mono />
          </Stack>
        </Paper>

        {/* Visualización del certificado */}
        <Paper sx={{ p: { xs: 2, md: 3 }, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="overline" color="text.secondary">Vista del certificado</Typography>
            {valido && (
              <Stack direction="row" spacing={1}>
                <Button size="small" startIcon={<Print />}
                        onClick={() => imprimirCertificado(cardRef.current, certificado.codigo)}>
                  Imprimir
                </Button>
                <Button size="small" variant="contained" startIcon={<Download />}
                        onClick={() => descargarCertificadoPng(cardRef.current, certificado.codigo)}>
                  Descargar PNG
                </Button>
              </Stack>
            )}
          </Box>

          {/* Escalado responsivo */}
          <Box sx={{
            overflow: 'auto', py: 2,
            display: 'flex', justifyContent: 'center',
          }}>
            <Box sx={{
              transform: { xs: 'scale(0.35)', sm: 'scale(0.5)', md: 'scale(0.7)', lg: 'scale(0.85)' },
              transformOrigin: 'top center',
              // Como escalamos, debemos reservar altura para que no corte
              mb: { xs: '-460px', sm: '-350px', md: '-215px', lg: '-105px' },
            }}>
              <CertificadoCard
                ref={cardRef}
                cert={certificado}
                plantilla={plantilla}
                urlValidacion={urlValidacion}
              />
            </Box>
          </Box>
        </Paper>

        {/* Pie: cómo verificar */}
        <Paper sx={{ p: 2, bgcolor: '#eef2f6' }}>
          <Typography variant="body2" color="text.secondary">
            Esta página se abrió a través del código QR impreso en el certificado.
            Si el banner superior muestra ✓, el certificado es auténtico y fue emitido por {certificado.institucionNombre || 'la institución señalada'}.
            Cada verificación queda registrada en el sistema con fines de auditoría.
          </Typography>
        </Paper>

        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Button href="/" startIcon={<OpenInNew />} size="small">
            Ir al portal
          </Button>
        </Box>
      </Container>
    </Box>
  );
}

function Dato({ label, value, mono }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="body2" sx={{
        fontWeight: 500, textAlign: 'right',
        fontFamily: mono ? 'monospace' : undefined,
      }}>
        {value || '—'}
      </Typography>
    </Box>
  );
}
